/**
 * search.js — Incremental PDF text search using PDF.js text content API.
 *
 * Searches page-by-page, calling a callback as results are found.
 * Searches each text item individually (no cross-item concatenation).
 */

const _textCache = new Map()

let _abortController = null

export function clearCache() {
  _textCache.clear()
}

export function cancel() {
  if (_abortController) {
    _abortController.abort()
    _abortController = null
  }
}

/**
 * Search incrementally, page by page.
 * @param {import('pdfjs-dist').PDFDocumentProxy} pdf
 * @param {string} query
 * @param {(result: {page:number, rects:object[]}) => void} onPageResult
 * @param {() => void} onDone
 */
export async function searchIncremental(pdf, query, onPageResult, onDone) {
  cancel()
  _abortController = new AbortController()
  const signal = _abortController.signal

  if (!query.trim()) { onDone(); return }
  const q = query.toLowerCase()

  for (let i = 0; i < pdf.numPages; i++) {
    if (signal.aborted) return
    try {
      const items = await _getPageItems(pdf, i)
      const matches = _findMatches(items, q)
      if (matches.length > 0) {
        onPageResult({
          page: i,
          rects: matches.map(m => m.rect),
          texts: matches.map(m => m.text),
        })
      }
    } catch (err) {
      console.warn('[search] page', i, err)
    }
  }
  if (!signal.aborted) onDone()
}

async function _getPageItems(pdf, pageIdx) {
  if (_textCache.has(pageIdx)) return _textCache.get(pageIdx)

  const page = await pdf.getPage(pageIdx + 1)
  const viewport = page.getViewport({ scale: 1.0 })
  const content = await page.getTextContent({ includeMarkedContent: false })

  const items = []
  for (const raw of content.items) {
    if (!raw.str || !raw.str.trim()) continue
    const result = _itemToRect(raw, viewport)
    if (result) items.push({ str: raw.str, rect: result.rect, vertical: result.vertical, startPt: result.startPt, endPt: result.endPt })
  }

  _textCache.set(pageIdx, items)
  return items
}

function _itemToRect(item, viewport) {
  if (!item.transform) return null
  const [a, b, c, d, tx, ty] = item.transform

  // Detect rotation from transform matrix
  const angle = Math.atan2(b, a)
  const vertical = Math.abs(Math.abs(angle) - Math.PI / 2) < 0.1

  // Text advance direction unit vector: (a, b) normalized
  const advLen = Math.sqrt(a * a + b * b) || 1
  const ux = a / advLen, uy = b / advLen

  // Perpendicular (height) direction unit vector: (c, d) normalized
  const hLen = Math.sqrt(c * c + d * d) || 1
  const vx = c / hLen, vy = d / hLen

  // item.width/height are in PDF user-space units (post-transform scale)
  const w = item.width || advLen * item.str.length * 0.5
  const h = item.height || hLen

  // Build 4 corners in PDF user space
  // c0 = baseline start (origin)
  // c1 = baseline end (advance by width along text direction)
  // c2 = ascender start (up by height)
  // c3 = ascender end (diagonal corner)
  const c0x = tx,                         c0y = ty
  const c1x = tx + ux * w,               c1y = ty + uy * w
  const c2x = tx + vx * h,               c2y = ty + vy * h
  const c3x = tx + ux * w + vx * h,      c3y = ty + uy * w + vy * h

  try {
    const v0 = viewport.convertToViewportPoint(c0x, c0y)
    const v1 = viewport.convertToViewportPoint(c1x, c1y)
    const v2 = viewport.convertToViewportPoint(c2x, c2y)
    const v3 = viewport.convertToViewportPoint(c3x, c3y)

    const xs = [v0[0], v1[0], v2[0], v3[0]]
    const ys = [v0[1], v1[1], v2[1], v3[1]]
    const minX = Math.min(...xs)
    const minY = Math.min(...ys)

    return {
      rect: { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY },
      vertical,
      // Viewport-space start and end of the text advance direction.
      // Used for accurate sub-rect interpolation in _findMatches.
      startPt: [v0[0], v0[1]],
      endPt:   [v1[0], v1[1]],
    }
  } catch {
    return null
  }
}

/** Match query within each text item. Compute sub-rect for just the matched chars. */
function _findMatches(items, query) {
  const results = []
  for (const item of items) {
    const lower = item.str.toLowerCase()
    let pos = 0
    while (pos < lower.length) {
      const idx = lower.indexOf(query, pos)
      if (idx === -1) break

      const totalLen = item.str.length
      const startFrac = idx / totalLen
      const endFrac = (idx + query.length) / totalLen

      // Interpolate along the actual advance vector (startPt → endPt in viewport space).
      // This correctly handles both horizontal and vertical (rotated) text.
      let subRect
      if (item.startPt && item.endPt) {
        const [sx, sy] = item.startPt
        const [ex, ey] = item.endPt
        const psx = sx + startFrac * (ex - sx)
        const psy = sy + startFrac * (ey - sy)
        const pex = sx + endFrac   * (ex - sx)
        const pey = sy + endFrac   * (ey - sy)

        if (item.vertical) {
          // Advance is mainly vertical — keep full X extent, slice Y
          subRect = {
            x: item.rect.x,
            y: Math.min(psy, pey),
            w: item.rect.w,
            h: Math.abs(pey - psy),
          }
        } else {
          // Advance is mainly horizontal — slice X, keep full Y extent
          subRect = {
            x: Math.min(psx, pex),
            y: item.rect.y,
            w: Math.abs(pex - psx),
            h: item.rect.h,
          }
        }
      } else if (item.vertical) {
        subRect = { x: item.rect.x, y: item.rect.y, w: item.rect.w, h: item.rect.h }
      } else {
        subRect = {
          x: item.rect.x + item.rect.w * startFrac,
          y: item.rect.y,
          w: item.rect.w * (endFrac - startFrac),
          h: item.rect.h,
        }
      }

      results.push({ rect: subRect, text: item.str })
      pos = idx + 1
    }
  }
  return results
}
